import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useFlow } from "@/contexts/FlowContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  CheckCircle2, ArrowRight, Shield, UserCheck, MessageSquare,
  RotateCcw, Globe, Briefcase, Check, ArrowLeft, Sparkles,
  ChevronRight, ShieldCheck, X, Mail, Loader2,
  Linkedin, Facebook, Lock,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import { useStepParam } from "@/hooks/useStepParam";
import { useSession } from "@/hooks/useSession";
import { usePublishSessionVertical } from "@/lib/sessionVertical";
import { usesPartnerLook } from "@/lib/flowPresentation";
import { copyForIndustry, isRecruiting, verticalFrom } from "@/lib/verticalCopy";
import {
  STATED_RELATIONSHIP_KEY,
  relationshipLabel,
  relationshipOptionsFor,
  relationshipValue,
} from "@/lib/relationshipOptions";
import { demoFlowFromParam, DEMO_LEASING_ORG, PREVIEW_ROUTE_NOTE, type DemoFlow } from "@/lib/demoFlows";
import { DemoEmailCodePanel, DemoIDVPanel } from "@/components/DemoVerificationPanels";
import { EmailCodePanel } from "@/components/EmailCodePanel";
import { offerableSources } from "@/lib/offerableSources";
import { applicantApi, voucherApi, type ApiError, type SignalSummary } from "@/lib/api";
import { toast } from "@/hooks/use-toast";
import { initClarity } from "@/lib/clarity";
import { PlaidIDVButton } from "@/components/PlaidIDVButton";
import { AccountChecksTracker } from "@/components/AccountChecksTracker";
import { useTenant } from "@/hooks/useTenant";
import { clearVoucherAnswers, loadVoucherAnswers, saveVoucherAnswers } from "@/lib/voucherAnswersDraft";
import {
  answerOpensUnableToConfirm,
  questionsContinueBlocked,
  questionsSubmitBlocked,
  unansweredQuestions,
  voucherLookRules,
} from "@/lib/voucherLookRules";
import { BrandIcon, resolveBrandKey } from "@/components/BrandIcon";

// Voucher flow — 5 steps. Step 0 is the merged Welcome + Intent screen
// (combines the former "Confirm your details" + "Verify your email" steps).
// The standalone email-verify step was removed in 2026-05 — the magic-link
// click already proves the invitation email at activate time on the backend.
// See docs/voucher-flow-step1-merged.md for the full handoff.
const steps = [
  { title: "Welcome", icon: Mail, desc: "Confirm your details and review what's next." },
  { title: "Identity verification", icon: Shield, desc: "Optional — upload an ID and selfie for stronger verification." },
  { title: "Link supporting accounts", icon: Globe, desc: "Optional — connect accounts to strengthen your corroboration." },
  { title: "Answer a few short questions", icon: MessageSquare, desc: "Help us understand your relationship with the applicant." },
  { title: "Submit", icon: CheckCircle2, desc: "Review and confirm your responses." },
];

// Voucher Step 4 (Questions) — keep `{first}` placeholders so copy stays in
// sync with the applicant's first name at render + payload time. `softOptions`
// renders the listed option in a muted "Not sure" style (mock parity).
// `qa_responses` is delivered as a flat { questionText: answer } map — backend
// stores it verbatim, so renaming questions here does NOT require a migration.
// See docs/voucher-flow-step4-questions.md.
//
// ⚠️ The QUESTION LIST and its indices are identical across verticals, and must
// stay that way: `answers` is indexed positionally and `qaPayload` is keyed by
// the rendered question text.
//
// A sixth question ("In what context do you know them?") was dropped va#177:
// it re-asked, in the voucher's own words, exactly what Step 0's read-only
// "Your relationship to {first}" field already shows — the applicant supplied
// that relationship when they invited this voucher, so asking it again here
// was a duplicate, not a second data point.
//
// A fifth question ("Based on what you personally know, does this seem like
// the {first} you know?") and the free-text note were dropped too (Juan and
// Patrick, 2026-10-15): the question asked the voucher for a character
// judgement, and an open text box invited comments a reference should not be
// asked for. Answers and notes are stored and shown to the operator only; no
// scoring reads either.
type VouchQuestion = { q: string; options: string[]; softOptions?: string[] };

function buildQuestions(): VouchQuestion[] {
  return [
    { q: "Do you personally know {first}?", options: ["Yes", "No"] },
    { q: "Did {first} personally ask you to complete this vouch?", options: ["Yes", "No", "Not sure"], softOptions: ["Not sure"] },
    { q: "How long have you known them?", options: ["Less than 1 year", "1–3 years", "3–5 years", "5+ years"] },
    { q: "Have you interacted with them recently?", options: ["Within the last 6 months", "Within the last year", "More than a year ago", "Not sure"], softOptions: ["Not sure"] },
  ];
}

interface VoucherSource {
  id: string;
  name: string;
  desc: string;
  tag?: string;
  icon: typeof Linkedin;
  iconBg: string;
  iconFg: string;
  liveOAuth?: "google" | "microsoft" | "linkedin" | "amazon" | "uber" | "facebook";
  isEmailOtp?: boolean;
  /**
   * Render only when the backend advertises `liveOAuth` in
   * `available_oauth_providers` (BACKEND_INTEGRATION.md §26).
   *
   * Opt-in per row, exactly as on the applicant side: rows that predate the
   * field keep rendering unconditionally, or an older backend (which
   * normalizes the list to empty) would blank the whole step.
   */
  gatedOnAvailability?: boolean;
}

// Voucher Step 2 (Connect Accounts) — flat, ordered row list. Backed by the
// existing OAuth + email-OTP wiring (no new endpoints). Amazon/Uber were
// intentionally dropped from this step in 2026-05; keep the liveOAuth
// vocabulary in sync if they come back. See docs/voucher-flow-step3-connect-accounts.md.
const voucherStep2Sources: VoucherSource[] = [
  {
    id: "v-linkedin",
    name: "LinkedIn",
    desc: "Professional history alignment",
    tag: "Recommended",
    icon: Linkedin,
    iconBg: "bg-[#0A66C2]/10",
    iconFg: "text-[#0A66C2]",
    liveOAuth: "linkedin",
  },
  {
    id: "v-work-email",
    name: "Work or School Email",
    desc: "Verify with a one-time code sent to your inbox.",
    tag: "Recommended",
    icon: Mail,
    iconBg: "bg-trust-green-muted",
    iconFg: "text-trust-green",
    isEmailOtp: true,
  },
  {
    id: "v-google",
    name: "Google",
    desc: "Confirms account ownership and tenure.",
    icon: Globe,
    iconBg: "bg-[#EA4335]/10",
    iconFg: "text-[#EA4335]",
    liveOAuth: "google",
  },
  {
    id: "v-microsoft",
    name: "Microsoft",
    desc: "Accepts Outlook, Hotmail, and Live addresses.",
    icon: Briefcase,
    iconBg: "bg-[#2F6FBA]/10",
    iconFg: "text-[#2F6FBA]",
    liveOAuth: "microsoft",
  },
  {
    id: "v-facebook",
    name: "Facebook",
    desc: "Identity-linked contact methods.",
    icon: Facebook,
    iconBg: "bg-[#1877F2]/10",
    iconFg: "text-[#1877F2]",
    // Was `isFacebook: true`, which opened the FB JS SDK and, on success, only
    // pushed the row id into local state — no backend call at all, not even the
    // the manual submit the applicant side made (retired, platform #442). So
    // the row showed "connected",
    // advanced the account-checks tracker under copy promising the applicant's
    // file got stronger, and vanished on refresh, having recorded nothing.
    // Facebook is a real server-side OAuth provider since platform #424, so
    // this is now the same path as every other row here.
    liveOAuth: "facebook",
    gatedOnAvailability: true,
  },
];

/** Render the voucher's confirmed DOB. Falls through to the raw string for any non-ISO value. */
function formatDobForDisplay(dob: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(dob)) {
    const [y, m, d] = dob.split("-");
    return `${m}/${d}/${y}`;
  }
  return dob;
}




export default function VoucherFlow({
  presentationFlow = null,
}: {
  presentationFlow?: DemoFlow | null;
} = {}) {
  const { state, setVoucherStep, completeVoucher, resetVoucherFlow } = useFlow();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { tenant } = useTenant();
  useEffect(() => { initClarity(); }, []);

  // The URL `token` is initially the invitation token. After we POST
  // /voucher/:token/activate, we get back a real session token which we
  // use for all subsequent /sessions/:token/... calls.
  const urlToken = searchParams.get("token") ?? null;
  const isLive = Boolean(urlToken);

  // Demo lane — `/demo/voucher-flow?flow=tech_role|high_volume`, the same
  // parameter and the same resolver the applicant lanes use, so a walkthrough
  // that crosses between the two surfaces names one org throughout.
  //
  // Demo-only by construction: a live voucher arrives with `?token=`, and this
  // is guarded on its absence.
  const implementationMode = !urlToken && presentationFlow !== null;
  const defaultDemoFlow = !urlToken ? demoFlowFromParam(searchParams.get("flow")) : null;
  const demoFlow = presentationFlow ?? defaultDemoFlow;

  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [activating, setActivating] = useState<boolean>(Boolean(urlToken));
  const [activateError, setActivateError] = useState<string | null>(null);
  const [activateErrorKind, setActivateErrorKind] =
    useState<"already_completed" | "expired" | "invalid" | "generic" | null>(null);
  const [vouchingFor, setVouchingFor] = useState<{
    applicant_name?: string;
    relationship?: string;
  } | null>(null);

  // Activate the invitation on mount. If the backend says it's already been
  // accepted, we treat the URL token as an existing session token (resume).
  useEffect(() => {
    if (!urlToken) {
      setActivating(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await voucherApi.activate(urlToken);
        if (cancelled) return;
        setSessionToken(res.session_token);
        setVouchingFor({
          applicant_name: res.applicant_name,
          relationship: res.relationship,
        });
        // Swap the URL token for the session token so refresh/back works.
        // Functional update: this races useStepParam's ?step= writes in the
        // mount window, and a stale snapshot here would drop a just-written
        // step (the clobber the hook's own writer already defends against).
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set("token", res.session_token);
            return next;
          },
          { replace: true },
        );
      } catch (e) {
        if (cancelled) return;
        const err = e as ApiError;
        if (err.status === 409 || err.code === "ACCEPTED") {
          // Already activated — the URL token IS the session token (resume).
          setSessionToken(urlToken);
        } else if (err.status === 410) {
          setActivateErrorKind("expired");
          setActivateError("This invitation has expired.");
        } else if (err.status === 404) {
          setActivateErrorKind("invalid");
          setActivateError("This invitation link is invalid.");
        } else {
          setActivateErrorKind("generic");
          setActivateError(err.message || "Couldn't open this invitation.");
        }
      } finally {
        if (!cancelled) setActivating(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlToken]);

  const liveToken = sessionToken;
  const { session, loading: sessionLoading, error: sessionError, refresh } =
    useSession(liveToken ?? undefined);

  // The vertical this page renders, from ONE place.
  //
  // Live: `org_industry` off /sessions/:token/status. Demo: the lane's own
  // industry — there is no session at all without a token, which is why this
  // flow rendered leasing copy to a recruiting referee no matter which lane
  // they had just walked. Undefined still resolves to leasing (`copyForIndustry`
  // calls `verticalFrom` internally), so the bare demo is unchanged.
  const industry = isLive ? session?.org_industry : demoFlow?.industry;

  // The partner look (lib/flowPresentation.ts): a partner tenant, or a live
  // recruiting session. A demo lane keeps its own look, so only a live
  // session's industry counts here, and the shell is told the same value.
  const liveIndustry = isLive ? industry : undefined;
  const articlePresentation = implementationMode || usesPartnerLook(tenant?.branding?.partner_key, liveIndustry);
  // The two voucher rules that differ per look (identity box blocks; every
  // question needs an answer). Per look for now; see CLAUDE.md, "The new look",
  // and voucherLookRules.ts.
  const lookRules = voucherLookRules(articlePresentation);

  // The question list no longer varies by vertical (va#177 dropped the one
  // question — "In what context do you know them?" — whose options used to
  // narrow for recruiting; see `buildQuestions`), so this no longer reads
  // `industry`.
  const questions = useMemo(
    () => buildQuestions(),
    [],
  );

  // Local view step (live mode keeps its own; demo mode uses DemoContext).
  // Initialized to null so we can detect "first-load" in the useEffect below
  // and jump the voucher past any steps they've already completed (instead of
  // forcing them back to "Confirm your details" every time the magic link is
  // re-opened).
  const [liveViewStep, setLiveViewStep] = useState<number | null>(null);
  const current = isLive ? (liveViewStep ?? 0) : state.voucherStep;
  const setStep = (n: number) => {
    if (isLive) setLiveViewStep(n);
    else setVoucherStep(n);
  };
  useStepParam(current, setStep, { max: steps.length - 1 });

  // Step 0: pre-filled details. Checkbox is pre-checked + non-blocking per
  // the merged-step spec — see docs/voucher-flow-step1-merged.md.
  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const [consenting, setConsenting] = useState(false);

  // Resume the voucher where they left off when the magic link is re-opened.
  // Two sources of truth, in priority order:
  //   1. dv_voucher_oauth_return_step in sessionStorage — set right before an
  //      OAuth handoff so we can return the voucher to the linked-accounts
  //      page (the step where they kicked off OAuth), not whatever step a
  //      "highest completed" heuristic would land on.
  //   2. session.steps_completed — tells us they've consented (skip Step 0)
  //      and/or linked an account (skip Step 3 to the questions step) when
  //      they're cold-opening the magic link, not returning from OAuth.
  // Only fires once per session-load: `prev === null` guards against yanking
  // the user backward after they navigate manually.
  useEffect(() => {
    if (!isLive || !session) return;
    const completed = session.steps_completed ?? [];
    const hasConsent = completed.includes("consent");
    if (hasConsent) {
      // Consent is gated behind the email-confirm checkbox on Step 0, so we
      // know the user already checked it. Reflect that in local state so the
      // checkbox renders as ticked if they navigate back.
      setEmailConfirmed(true);
    }

    // If the backend already has a verified IDV for this voucher (they passed,
    // then reopened the link — idvDone is browser-local and resets on reload),
    // reflect it so step 1 doesn't re-gate or ask them to redo IDV. The step-1
    // gate + the PlaidIDVButton "verified" state both derive from this.
    if (session.voucher_idv_verified) {
      setDataSourceVerified(true);
    }

    // Prefer the OAuth-return step if we stashed one before the redirect.
    let oauthReturnStep: number | null = null;
    try {
      const stashed = sessionStorage.getItem("dv_voucher_oauth_return_step");
      if (stashed !== null) {
        const parsed = parseInt(stashed, 10);
        if (!Number.isNaN(parsed) && parsed >= 0 && parsed < steps.length) {
          oauthReturnStep = parsed;
        }
        // Single-use — clear so a subsequent magic-link click doesn't reuse it.
        sessionStorage.removeItem("dv_voucher_oauth_return_step");
      }
    } catch { /* ignore */ }

    // Step indexes after the 5-step merge:
    //   0 Welcome+Intent · 1 ID · 2 Link accounts · 3 Questions · 4 Submit
    let furthest = 0;
    if (hasConsent) furthest = 1;
    if (completed.some((s) => s === "email" || s.startsWith("oauth_") || s.startsWith("social_"))) {
      furthest = 3; // past linked-accounts → land on the questions step
    }
    setLiveViewStep((prev) => {
      if (prev !== null) return prev;
      return oauthReturnStep ?? furthest;
    });
  }, [isLive, session]);

  // Step 1: ID verification
  const [idUploaded, setIdUploaded] = useState(false);
  const [selfieCompleted, setSelfieCompleted] = useState(false);
  // Plaid's data-source (KYC) path verifies identity WITHOUT a document+selfie
  // (it's the path the prod template runs first). A pass here satisfies the IDV
  // gate on its own — see PlaidIDVButton onSuccess + the gate below.
  const [dataSourceVerified, setDataSourceVerified] = useState(false);

  // Step 3: linked accounts
  const [connectedVoucherSources, setConnectedVoucherSources] = useState<string[]>([]);
  // expandedCategory state removed in step-2 redesign (flat list, no accordion).
  const [pendingSource, setPendingSource] = useState<VoucherSource | null>(null);
  const [oauthLoading, setOauthLoading] = useState(false);

  // Step 4: questions
  const [answers, setAnswers] = useState<Record<number, string>>({});
  // Article keeps the existing five-stage journey while presenting the
  // questions one at a time inside stage four. Answers remain keyed by their
  // original question index, so the submission payload is unchanged.
  const [articleQuestionIndex, setArticleQuestionIndex] = useState(0);
  // Questions that still hold Submit back. Empty where partial answers are
  // accepted (`lookRules.everyAnswerRequired` false).
  const missingAnswers = lookRules.everyAnswerRequired ? unansweredQuestions(answers, questions.length) : [];

  // Submission state (live). `unable_to_confirm` is a frontend-only flag today —
  // backend still accepts only "confirm" | "decline". See
  // docs/voucher-flow-step4-unable-to-confirm.md for the rewiring spec.
  const [submitting, setSubmitting] = useState<null | "confirm" | "decline" | "unable">(null);
  const [liveSubmitted, setLiveSubmitted] = useState(false);
  const [liveOutcome, setLiveOutcome] = useState<"confirm" | "decline" | "unable_to_confirm" | null>(null);

  // Answers and the note live in this page, so a reload used to lose them
  // while ?step= kept the voucher on the review screen. Keep them for this
  // live voucher session (sessionStorage, keyed by the session token, with the
  // question texts they answer) and restore them once the token is known. The
  // demo keeps nothing. Cleared on any terminal outcome; see voucherAnswersDraft.
  const questionTexts = useMemo(() => questions.map((q) => q.q), [questions]);
  const [answersRestoredFor, setAnswersRestoredFor] = useState<string | null>(null);
  // A layout effect, not a passive one: the token arrives from an async
  // activate, and a passive effect can run after the review screen is already
  // on screen, so a quick Submit went out without the restored answers. A
  // layout effect restores them in the same commit, before any click.
  useLayoutEffect(() => {
    if (!isLive || !liveToken || answersRestoredFor === liveToken) return;
    const kept = loadVoucherAnswers(liveToken, questionTexts);
    if (kept) setAnswers(kept.answers);
    setAnswersRestoredFor(liveToken);
  }, [isLive, liveToken, questionTexts, answersRestoredFor]);
  // Set the moment an outcome is recorded, before any state update: a save
  // effect queued by an earlier render can still run after the clear, and it
  // closes over the old (empty) outcome, so the state alone would let it
  // write the draft back.
  const answersFinished = useRef(false);
  const finishAnswers = (token: string) => {
    answersFinished.current = true;
    clearVoucherAnswers(token);
  };
  useEffect(() => {
    if (!isLive || !liveToken || answersRestoredFor !== liveToken || liveOutcome) return;
    if (answersFinished.current) return;
    // No note any more (2026-10-15); the draft format keeps the field.
    saveVoucherAnswers(liveToken, questionTexts, answers, "");
  }, [isLive, liveToken, questionTexts, answersRestoredFor, liveOutcome, answers]);
  // Step 0 tertiary "I don't know this person" decline confirmation dialog.
  const [declineDialog, setDeclineDialog] = useState(false);
  // Step 4 Q1=No confirmation modal.
  const [noKnowDialog, setNoKnowDialog] = useState(false);

  // Reflect backend connected providers into the linked-accounts UI.
  useEffect(() => {
    if (!session?.connected_providers?.length) return;
    const ids = session.connected_providers
      .filter((p) => p.status === "connected")
      .map((p) => `v-${p.provider.toLowerCase()}`);
    setConnectedVoucherSources((prev) => Array.from(new Set([...prev, ...ids])));
  }, [session?.connected_providers]);

  const isSourceConnected = (id: string) => connectedVoucherSources.includes(id);

  const handleSourceClick = (src: VoucherSource) => {
    // Work/School Email is always treated as "add a new email" — clicking
    // it re-opens the OTP dialog even if a prior email was verified here.
    // Pre-existing session emails (invitation email, applicant-step emails)
    // never count for this slot.
    if (src.isEmailOtp) {
      setPendingSource(src);
      return;
    }
    if (isSourceConnected(src.id)) {
      setConnectedVoucherSources((prev) => prev.filter((s) => s !== src.id));
    } else {
      setPendingSource(src);
    }
  };


  const confirmConnect = async () => {
    if (!pendingSource) return;
    if (isLive && liveToken && pendingSource.liveOAuth) {
      try {
        setOauthLoading(true);
        // Stash the step the voucher was on so the OAuth callback can return
        // them to the linked-accounts page (where they see "✓ Connected") and
        // not whatever step our 'highest completed' heuristic picks. Mirrors
        // the dv_oauth_return_step pattern used by ApplicantFlow.
        try {
          sessionStorage.setItem("dv_voucher_oauth_return_step", String(liveViewStep ?? 2));
        } catch { /* ignore — sessionStorage can throw in private mode */ }
        const { redirect_url } = await applicantApi.startOAuth(liveToken, pendingSource.liveOAuth);
        // PUSH, never replace — same reasoning as ApplicantFlow's startOAuth.
        // `replace()` overwrites OUR entry, not the provider's, so Back from
        // the provider leaves the flow entirely instead of returning to it.
        // A voucher is doing someone else a favour; losing their place is the
        // one thing most likely to make them abandon it.
        window.location.href = redirect_url;
        return;
      } catch (e) {
        const err = e as ApiError;
        toast({ title: "Couldn't start sign-in", description: err.message, variant: "destructive" });
        setOauthLoading(false);
        return;
      }
    }
    setConnectedVoucherSources((prev) => [...prev, pendingSource.id]);
    setPendingSource(null);
  };


  const handleConsent = async () => {
    if (!liveToken) return;
    try {
      setConsenting(true);
      await applicantApi.giveConsent(liveToken);
      await refresh();
      // After consent, advance to step 1 (Identity verification). The former
      // "Verify your email" step was removed in the 5-step merge — the
      // magic-link click already proves the invitation email at activate
      // time on the backend. See docs/voucher-flow-step1-merged.md.
      setLiveViewStep(1);
      toast({ title: "Consent recorded", description: "Thanks — let's continue." });
    } catch (e) {
      const err = e as ApiError;
      toast({ title: "Couldn't record consent", description: err.message, variant: "destructive" });
    } finally {
      setConsenting(false);
    }
  };

  const handleSubmitOutcome = async (outcome: "confirm" | "decline") => {
    if (!liveToken) {
      setLiveOutcome(outcome);
      completeVoucher();
      return;
    }
    try {
      setSubmitting(outcome);
      const qaPayload: Record<string, string> = {};
      questions.forEach((q, i) => {
        if (answers[i]) {
          const key = q.q.replace(/\{first\}/g, applicantFirstName || "them");
          qaPayload[key] = answers[i];
        }
      });
      Object.assign(qaPayload, statedRelationshipEntry());
      await applicantApi.submitVouchOutcome(liveToken, {
        outcome,
        qa_responses: Object.keys(qaPayload).length > 0 ? qaPayload : undefined,
      });
      finishAnswers(liveToken);
      setLiveOutcome(outcome);
      setLiveSubmitted(true);
      await refresh();
      toast({
        title: outcome === "confirm" ? "Vouch confirmed" : "Response recorded",
        description: outcome === "confirm"
          ? "Thank you for confirming."
          : "Thank you — your response was recorded.",
      });
    } catch (e) {
      const err = e as ApiError;
      if (err.status === 409 || err.code === "ALREADY_SUBMITTED") {
        finishAnswers(liveToken);
        setLiveOutcome(outcome);
        setLiveSubmitted(true);
        return;
      }
      toast({
        title: "Couldn't submit your response",
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setSubmitting(null);
    }
  };

  // Step 4 — Q1 "No" path. Voucher cannot confirm the applicant. Sends the
  // first-class `unable_to_confirm` outcome with a structured `reason`; the
  // backend stores the reason on the invitation and writes no positive signal
  // (it no longer rides on the `decline` outcome + a free-text notes prefix).
  const handleUnableToConfirm = async () => {
    if (!liveToken) {
      setLiveOutcome("unable_to_confirm");
      completeVoucher();
      setNoKnowDialog(false);
      return;
    }
    try {
      setSubmitting("unable");
      await applicantApi.submitVouchOutcome(liveToken, {
        outcome: "unable_to_confirm",
        reason: "does_not_know_applicant",
        qa_responses: {
          [questions[0].q.replace(/\{first\}/g, applicantFirstName || "them")]: "No",
          ...statedRelationshipEntry(),
        },
      });
      finishAnswers(liveToken);
      setLiveOutcome("unable_to_confirm");
      setLiveSubmitted(true);
      setNoKnowDialog(false);
      await refresh();
    } catch (e) {
      const err = e as ApiError;
      if (err.status === 409 || err.code === "ALREADY_SUBMITTED") {
        finishAnswers(liveToken);
        setLiveOutcome("unable_to_confirm");
        setLiveSubmitted(true);
        setNoKnowDialog(false);
        return;
      }
      toast({ title: "Couldn't record your response", description: err.message, variant: "destructive" });
    } finally {
      setSubmitting(null);
    }
  };

  const handleNext = () => {
    if (current >= 4) return; // Step 4 (Submit) uses Confirm/Decline buttons directly
    setStep(current + 1);
  };

  const handleReset = () => {
    resetVoucherFlow();
    // FALSE, not true. A fresh voucher arrives with this unticked (see
    // CLAUDE.md, voucher step 0) — resetting to ticked left the consent box
    // already agreed to, which is the one thing on step 0 a viewer is meant to
    // do themselves.
    setEmailConfirmed(false);
    setIdUploaded(false);
    setSelfieCompleted(false);
    setConnectedVoucherSources([]);
    // expandedCategory removed
    setAnswers({});
    setArticleQuestionIndex(0);
    if (isLive) {
      setLiveViewStep(0);
      setLiveSubmitted(false);
    }
  };

  // The rows this environment can actually complete a flow for. Gating happens
  // at RENDER only — the counts below deliberately stay over the full list, so
  // an already-connected provider keeps counting even if the backend later
  // stops advertising it. A connection that exists is evidence that exists.
  const offerableRows = useMemo(
    () =>
      offerableSources(voucherStep2Sources, {
        availableOAuth: session?.available_oauth_providers ?? [],
      }),
    [session?.available_oauth_providers],
  );

  // Only count accounts that are part of the step-2 source list. Pre-existing
  // session.connected_providers (e.g. the invitation email auto-attested at
  // activate time) must NOT inflate the visible "connected accounts" count.
  const step2ConnectedSources = voucherStep2Sources.filter((s) => connectedVoucherSources.includes(s.id));
  const voucherConnectedCount = step2ConnectedSources.length;
  // Account-check tier count for the tracker — OAuth providers only.
  // Email OTP (work/school email) is a supporting signal but does not move
  // the 1/2/3+ tier, matching the applicant-side rule.
  const voucherAccountChecks = step2ConnectedSources.filter((s) => !s.isEmailOtp).length;

  // Verified emails for this voucher's session — drives the green chips at
  // the top of step 1 (Verify your email). The invitation email is
  // auto-attested at activate time (backend writes a contact.email signal
  // with confidence 0.85), so on a fresh live session this list always
  // contains at least the magic-link email. Any additional emails the
  // voucher verifies via OTP show up here on subsequent /status refreshes.
  const verifiedVoucherEmails = useMemo<SignalSummary[]>(() => {
    const fromSignals = (session?.signals_summary ?? []).filter(
      (s) => s.key.toLowerCase().includes("email") && s.status === "verified",
    );
    const fromProviders = (session?.connected_providers ?? [])
      .filter((p) => p.provider.toLowerCase() === "email" && p.status === "connected")
      .map<SignalSummary>((p, i) => ({
        key: `contact_email_${i}`,
        label: "Email Verification",
        status: "verified",
        detail: p.masked_identifier,
      }));
    const seen = new Set<string>();
    return [...fromSignals, ...fromProviders].filter((s) => {
      const key = (s.detail ?? s.key).toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [session]);

  // Display values — live mode pulls from session/activate, demo uses persona.
  const orgName = isLive
    ? session?.org_name ?? "the organization"
    : demoFlow?.orgName ?? DEMO_LEASING_ORG;
  // The org's vertical, off the SAME endpoint. `voucherApi.activate` hands back
  // a session token that resolves against `/sessions/:token/status`, so the one
  // field serves the voucher flow and the applicant flow alike — no new call,
  // no new endpoint. Absent (demo mode, an older server) resolves to leasing,
  // which is what every string below said before this existed.
  //
  // ⚠️ This flow had NO vertical fork at all — proxy 5, the "none" row. A job
  // candidate's referee was reading tenant-screening copy end to end.
  const copy = copyForIndustry(industry);
  usePublishSessionVertical(
    liveIndustry,
    !isLive || session !== null || sessionError !== null || activateError !== null,
  );
  // `applicant_name` on the session is actually the voucher's own name once
  // /status exposes it; the activate response gives us who they're vouching FOR.
  const voucherName = isLive ? session?.applicant_name ?? "" : "Sarah Chen";
  const liveVouchingFor =
    session?.vouching_for?.applicant_name ?? vouchingFor?.applicant_name ?? null;
  const liveRelationship =
    session?.vouching_for?.relationship ?? vouchingFor?.relationship ?? null;
  const invitedRelationship = isLive ? liveRelationship : "Employer";
  // The voucher may correct the relationship the applicant picked (Juan,
  // 2026-10-15). Recorded in `qa_responses` under STATED_RELATIONSHIP_KEY and
  // shown, never scored: the invitation's own relationship, which scoring
  // reads, is not changed.
  const [statedRelationship, setStatedRelationship] = useState<string | null>(null);
  const chosenRelationshipValue = statedRelationship ?? relationshipValue(invitedRelationship);
  const displayRelationship = chosenRelationshipValue ? relationshipLabel(chosenRelationshipValue) : invitedRelationship;
  const relationshipChoices = (() => {
    const options = relationshipOptionsFor(isRecruiting(verticalFrom(industry)));
    const invitedValue = relationshipValue(invitedRelationship);
    // Keep the applicant's pick on the list even where this vertical would
    // not offer it, so the first screen never shows a blank choice.
    return invitedValue && !options.some((o) => o.value === invitedValue)
      ? [{ value: invitedValue, label: relationshipLabel(invitedValue) }, ...options]
      : options;
  })();
  const relationshipSelect = (className: string, id: string) => (
    <select
      id={id}
      aria-label={`Your relationship to ${applicantFirstName}`}
      className={className}
      value={chosenRelationshipValue}
      onChange={(event) => setStatedRelationship(event.target.value)}
    >
      {relationshipChoices.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
  // The voucher's own account, for `qa_responses`. Always sent on a live
  // outcome, changed or not, so the record says what the voucher affirmed.
  const statedRelationshipEntry = () =>
    chosenRelationshipValue ? { [STATED_RELATIONSHIP_KEY]: relationshipLabel(chosenRelationshipValue) } : {};
  const applicantDisplayName = isLive
    ? liveVouchingFor ?? "the applicant"
    : "Juan Alvarez";
  const applicantFirstName = applicantDisplayName.split(/\s+/)[0] || applicantDisplayName;
  const applicantInitials =
    applicantDisplayName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? "")
      .join("") || "?";
  const voucherDisplayName = voucherName || "you";
  // Does THIS org actually require the ID check for a voucher? Mirrors the
  // Step-1 Continue gate's own predicate exactly (`!idvStatusKnown ||
  // session?.voucher_idv_required === true`, `!session` standing in for
  // `!idvStatusKnown` since demo mode short-circuits below): while the
  // session is still loading, unknown reads as required (fail-closed, same
  // as the gate); once it has loaded, "required" means the backend
  // EXPLICITLY said so — undefined/false (the documented default,
  // BACKEND_INTEGRATION.md §24.1) is NOT required. Getting this backwards
  // would reintroduce platform#605 for every org that never set the flag.
  // Demo mode has no real org config and keeps today's always-required
  // framing.
  const idvRequiredForOrg = isLive ? (!session || session.voucher_idv_required === true) : true;
  // Did THIS voucher's identity actually get verified? Never inferred from
  // org policy — a required check can still be skipped after max retries,
  // and an org that doesn't require it can still have a voucher who
  // completed it anyway. platform#605.
  const identityVerified = (idUploaded && selfieCompleted) || dataSourceVerified;

  // The session is "completed" once the voucher submits.
  const showCompleted = isLive ? liveSubmitted || session?.status === "completed" : state.voucherCompleted;

  if (isLive && (activating || (sessionLoading && !session))) {
    return (
      <div className="max-w-2xl mx-auto py-16 flex items-center justify-center text-muted-foreground gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading invitation…
      </div>
    );
  }

  if (isLive && activateError) {
    const heading =
      activateErrorKind === "already_completed"
        ? "Already completed"
        : activateErrorKind === "expired"
          ? "Invitation expired"
          : activateErrorKind === "invalid"
            ? "Invalid invitation"
            : "Invitation unavailable";
    return (
      <div className="max-w-2xl mx-auto py-16 text-center space-y-2">
        <h1 className="text-xl font-display font-bold text-foreground">{heading}</h1>
        <p className="text-sm text-muted-foreground">{activateError}</p>
      </div>
    );
  }

  if (isLive && sessionError && !session) {
    return (
      <div className="max-w-2xl mx-auto py-16 text-center space-y-2">
        <h1 className="text-xl font-display font-bold text-foreground">Invitation unavailable</h1>
        <p className="text-sm text-muted-foreground">{sessionError.message}</p>
      </div>
    );
  }

  return (
    <div className={`max-w-2xl mx-auto space-y-6 ${articlePresentation ? "article-flow article-voucher-flow" : ""}`}>
      {implementationMode && <p className="demo-preview-note" role="note">{PREVIEW_ROUTE_NOTE}</p>}
      {!isLive && !implementationMode && (
        <>
          <div className="rounded-lg border border-violet-300/60 bg-violet-50/70 px-4 py-3 flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-violet-700 shrink-0 mt-0.5" aria-hidden />
            <div className="flex-1">
              <div className="text-sm font-semibold text-violet-900">Demo mode · Voucher View</div>
              <p className="text-xs text-violet-900/80 mt-0.5">
                This is a sample voucher flow for demonstration. No actions are saved.
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate("/demo")}
              className="shrink-0 bg-white"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Exit demo
            </Button>
          </div>
          <button
            onClick={() => navigate("/demo")}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to demos
          </button>
        </>
      )}
      {/* Confirmation Dialog */}
      <Dialog open={!!pendingSource} onOpenChange={(open) => !open && !oauthLoading && setPendingSource(null)}>
        <DialogContent className={articlePresentation ? "article-dialog sm:max-w-md" : "sm:max-w-md"}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-trust-green" />
              {pendingSource?.isEmailOtp ? `Verify ${pendingSource?.name}` : `Connect ${pendingSource?.name}`}
            </DialogTitle>
            <DialogDescription className="space-y-3 pt-2">
              {pendingSource?.isEmailOtp ? (
                <p>Enter your {pendingSource?.name.toLowerCase()} address. We'll send a verification code to confirm ownership.</p>
              ) : (
                <>
                  <p>
                    We only verify <span className="font-medium text-foreground">account ownership and basic metadata</span> such as account age and continuity.
                  </p>
                  <div className="bg-muted/50 rounded-lg p-3 space-y-1.5 text-xs">
                    <p className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-trust-green shrink-0" /> We confirm you own this account</p>
                    <p className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-trust-green shrink-0" /> We check account age and consistency</p>
                    <p className="flex items-center gap-2"><X className="w-3.5 h-3.5 text-destructive shrink-0" /> We do not read messages, posts, or personal content</p>
                    <p className="flex items-center gap-2"><X className="w-3.5 h-3.5 text-destructive shrink-0" /> We do not store passwords or credentials</p>
                  </div>
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          {/* Email OTP panel — shown inline in dialog body */}
          {pendingSource?.isEmailOtp && isLive && liveToken && (
            <div className="px-1">
              <EmailCodePanel
                token={liveToken}
                onVerified={async () => {
                  setConnectedVoucherSources((prev) => [...prev, pendingSource.id]);
                  setPendingSource(null);
                  await refresh();
                }}
              />
            </div>
          )}

          {/* Demo: `EmailCodePanel` needs a token to send a code, so this
              dialog opened with an empty body — the row looked connectable and
              then did nothing. Same stand-in the applicant demo uses. */}
          {pendingSource?.isEmailOtp && !isLive && articlePresentation && (
            <div className="article-voucher-email-check">
              <Mail aria-hidden="true" />
              <p>A one-time code confirms access to your work or school email.</p>
              <Button
                type="button"
                onClick={() => {
                  setConnectedVoucherSources((prev) => [...prev, pendingSource.id]);
                  setPendingSource(null);
                }}
              >
                Verify email
              </Button>
            </div>
          )}
          {pendingSource?.isEmailOtp && !isLive && !articlePresentation && (
            <div className="px-1">
              <DemoEmailCodePanel
                presentation={implementationMode ? "implementation" : "demo"}
                onVerified={() => {
                  setConnectedVoucherSources((prev) => [...prev, pendingSource.id]);
                  setPendingSource(null);
                }}
              />
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setPendingSource(null)} disabled={oauthLoading}>
              {pendingSource?.isEmailOtp ? "Close" : "Cancel"}
            </Button>
            {/* Facebook used to render the FB SDK button here instead of the
                standard "Connect". It is an ordinary `liveOAuth` row now, so
                `confirmConnect` handles it like LinkedIn/Google/Microsoft. */}
            {!pendingSource?.isEmailOtp && (
              <Button onClick={confirmConnect} disabled={oauthLoading}>
                {oauthLoading
                  ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Redirecting…</>
                  : <><ShieldCheck className="w-4 h-4 mr-1" /> Connect securely</>}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Step 0 tertiary decline confirmation. Reuses handleSubmitOutcome
          so the live path POSTs the same decline outcome as step 4. */}
      <Dialog open={declineDialog} onOpenChange={(open) => !open && submitting === null && setDeclineDialog(false)}>
        <DialogContent className={articlePresentation ? "article-dialog" : undefined}>
          <DialogHeader>
            <DialogTitle>Decline to vouch for {applicantFirstName}?</DialogTitle>
            <DialogDescription>
              You're saying you don't know {applicantFirstName} well enough — or aren't comfortable vouching for them. We'll record your response and {applicantFirstName} won't be told whether you declined.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setDeclineDialog(false)}
              disabled={submitting !== null}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                await handleSubmitOutcome("decline");
                setDeclineDialog(false);
              }}
              disabled={submitting !== null}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {submitting === "decline"
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Recording…</>
                : "Yes, decline to vouch"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Step 4 Q1 = "No" confirmation. Calm, non-accusatory framing. Primary
          path records the session as unable_to_confirm (rides on `decline`
          outcome until backend ships a first-class outcome — see
          docs/voucher-flow-step4-unable-to-confirm.md). Secondary link resets
          Q1 to empty (does NOT auto-flip to Yes). */}
      <Dialog open={noKnowDialog} onOpenChange={(open) => !open && submitting === null && setNoKnowDialog(false)}>
        <DialogContent className={articlePresentation ? "article-dialog" : undefined}>
          <DialogHeader>
            <DialogTitle>It's okay if you don't know them</DialogTitle>
            <DialogDescription className="space-y-3 pt-1">
              <span className="block">
                Thanks for letting us know. Since you don't personally know {applicantFirstName}, we won't ask you to complete a vouch.
              </span>
              <span className="block">
                Your response will be shared with the {copy.reviewingTeam} as <span className="font-semibold text-foreground">unable to confirm</span>.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col gap-2 sm:flex-col sm:gap-2 sm:space-x-0">
            <Button
              onClick={handleUnableToConfirm}
              disabled={submitting !== null}
              className="w-full"
            >
              {submitting === "unable"
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Recording…</>
                : "Close — I can't vouch here"}
            </Button>
            <button
              type="button"
              onClick={() => {
                setAnswers((prev) => {
                  const next = { ...prev };
                  delete next[0];
                  return next;
                });
                setNoKnowDialog(false);
              }}
              disabled={submitting !== null}
              className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
            >
              Go back — I tapped No by mistake
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {articlePresentation && !showCompleted ? (() => {
        const hero = [
          { h1: `A quick vouch for ${applicantFirstName}.`, h2: `Confirm the invitation, then share only what you personally know.` },
          { h1: "Confirm who you are.", h2: `A quick identity check makes this vouch more useful to ${orgName}.` },
          { h1: "A little more you.", h2: "Connect an account you already use to help confirm it’s you." },
          { h1: `Share what you know about ${applicantFirstName}.`, h2: "Answer one short question at a time." },
          { h1: "Review your vouch.", h2: `Check your answers before sharing them with ${orgName}.` },
        ][current];
        return (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={`article-flow-intro article-voucher-hero is-${["welcome", "identity", "accounts", "questions", "review"][current]} text-left space-y-2`}
          >
            <div className="article-flow-meta">
              <button
                type="button"
                disabled={current === 0}
                onClick={() => {
                  if (current === 3 && articleQuestionIndex > 0) {
                    setArticleQuestionIndex((index) => index - 1);
                    return;
                  }
                  if (current > 0) setStep(current - 1);
                }}
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <span>{["Your vouch", "Identity", "Digital accounts", "Questions", "Review"][current]} <strong>{current + 1} / {steps.length}</strong></span>
            </div>
            <div className="article-flow-progress" role="progressbar" aria-label="Vouch progress" aria-valuemin={1} aria-valuemax={steps.length} aria-valuenow={current + 1}>
              {steps.map((_, index) => <span key={index} className={index <= current ? "is-filled" : ""} />)}
            </div>
            <h1 className="text-3xl font-display font-semibold text-foreground">{hero.h1}</h1>
            <p className="text-base text-muted-foreground max-w-xl">{hero.h2}</p>
          </motion.div>
        );
      })() : current === 0 && !showCompleted ? (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-card border border-border text-xs font-semibold text-foreground/80 mb-7">
            <span className="w-1.5 h-1.5 rounded-full bg-trust-green" />
            Vouching for {applicantFirstName} · {orgName}
            {!isLive && (
              <>
                {!implementationMode && (
                  <button onClick={() => navigate("/demo")} className="ml-1 text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                    <ArrowLeft className="w-3 h-3" /> Exit demo
                  </button>
                )}
                <button onClick={handleReset} className="ml-1 text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                  <RotateCcw className="w-3 h-3" /> Reset
                </button>
              </>
            )}
          </div>
          <div
            className="w-[68px] h-[68px] rounded-full flex items-center justify-center text-white text-2xl font-extrabold mb-4"
            style={{
              background: "var(--flow-avatar-bg, linear-gradient(135deg, #7C3AED, #A78BFA))",
              boxShadow: "var(--flow-avatar-shadow, 0 4px 20px rgba(124,58,237,0.25))",
            }}
          >
            {applicantInitials}
          </div>
          <div className="text-[13px] font-semibold uppercase tracking-[0.08em] text-primary mb-2">
            {applicantFirstName} chose you as a trusted vouch
          </div>
          <h1 className="text-2xl sm:text-[28px] font-display font-extrabold text-foreground leading-tight mb-2.5">
            {copy.voucherHeroHeadline}
          </h1>
          <p className="text-[15px] text-muted-foreground leading-relaxed max-w-[460px] mb-3">
            They listed you as someone who knows them. Your quick {idvRequiredForOrg ? "verified " : ""}vouch helps the {copy.reviewingTeam} better understand their application and move it forward with confidence.
          </p>
          <div className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full bg-trust-green-muted border border-trust-green/30 text-[13px] font-semibold text-trust-green">
            <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            Takes about 2 minutes
          </div>
        </motion.div>
      ) : isLive && !showCompleted && current === 4 ? (
        /* Step 5 of 5 — celebration hero (NR ring with green ✓ badge, green eyebrow). */
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center text-center">
          <div
            className="relative w-[88px] h-[88px] rounded-full flex items-center justify-center text-white text-[30px] font-extrabold mb-[22px]"
            style={{
              background: "var(--flow-avatar-bg, linear-gradient(135deg, #7C3AED, #A78BFA))",
              boxShadow: "var(--flow-avatar-complete-shadow, 0 0 0 8px rgba(124,58,237,0.12), 0 4px 24px rgba(124,58,237,0.3))",
            }}
          >
            {applicantInitials}
            <span className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-trust-green border-2 border-background flex items-center justify-center">
              <Check className="w-3.5 h-3.5 text-trust-green-foreground" strokeWidth={3} />
            </span>
          </div>
          <div className="text-[13px] font-bold uppercase tracking-[0.1em] text-trust-green mb-2.5">
            You're ready to submit
          </div>
          <h1 className="text-2xl sm:text-[28px] font-display font-extrabold text-foreground leading-tight mb-3">
            You helped {applicantFirstName} move forward.
          </h1>
          <p className="text-[15px] text-muted-foreground leading-relaxed max-w-[460px]">
            Your {identityVerified ? "verified " : ""}vouch helps the {copy.reviewingTeam} better understand {applicantFirstName}'s application. Take a moment to review what you've shared, then submit when you're ready.
          </p>
        </motion.div>
      ) : isLive && !showCompleted && (current === 1 || current === 2 || current === 3) ? (
        (() => {
          const heroByStep: Record<number, { eyebrow: string; headline: string; sub: string }> = {
            1: {
              eyebrow: "Verify your identity",
              headline: `Make your vouch count for ${applicantFirstName}.`,
              sub: `A quick ID check ties your vouch to a real, verified person — so the ${copy.reviewingTeam} can give it the weight it deserves. We use it only to confirm your identity — checking details like your name and date of birth against your ID — never for anything else.`,
            },
            2: {
              eyebrow: "Connect an account",
              headline: `Help ${applicantFirstName} move forward with confidence.`,
              sub: `Connect one account so the ${copy.reviewingTeam} can confirm this vouch comes from a real, established person. We only check basic account signals like name and account history. We never read messages, contacts, purchases, or private content.`,
            },
            3: {
              eyebrow: "Share what you know",
              headline: `Tell us what you know about ${applicantFirstName}.`,
              sub: `A few quick questions about how you know ${applicantFirstName}. Answer only what you're comfortable with — "Not sure" is always an acceptable answer.`,
            },
          };
          const hero = heroByStep[current];
          return (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center text-center">
              <div
                className="w-[68px] h-[68px] rounded-full flex items-center justify-center text-white text-2xl font-extrabold mb-4"
                style={{
                  background: "var(--flow-avatar-bg, linear-gradient(135deg, #7C3AED, #A78BFA))",
                  boxShadow: "var(--flow-avatar-shadow, 0 4px 20px rgba(124,58,237,0.25))",
                }}
              >
                {applicantInitials}
              </div>
              <div className="text-[13px] font-semibold uppercase tracking-[0.08em] text-primary mb-2">
                Step {current + 1} of {steps.length} · {hero.eyebrow}
              </div>
              <h1 className="text-2xl sm:text-[28px] font-display font-extrabold text-foreground leading-tight mb-2.5">
                {hero.headline}
              </h1>
              <p className="text-[15px] text-muted-foreground leading-relaxed max-w-[460px]">
                {hero.sub}
              </p>
            </motion.div>
          );
        })()
      ) : showCompleted ? null : (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center space-y-2">
        <div className="flex items-center justify-center gap-2">
          <div className="inline-flex items-center gap-2 bg-trust-green-muted text-trust-green px-3 py-1 rounded-full text-xs font-medium">
            <UserCheck className="w-3.5 h-3.5" /> Voucher View
          </div>
          {!isLive && (
            <>
              {!implementationMode && (
                <Button variant="ghost" size="sm" onClick={() => navigate("/demo")} className="text-xs text-muted-foreground gap-1">
                  <ArrowLeft className="w-3 h-3" /> Exit demo
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={handleReset} className="text-xs text-muted-foreground gap-1">
                <RotateCcw className="w-3 h-3" /> Reset
              </Button>
            </>
          )}
        </div>
        <h1 className="text-2xl font-display font-bold text-foreground">
          Vouch for {applicantDisplayName}
        </h1>
        <p className="text-sm text-muted-foreground max-w-lg mx-auto">
          {isLive
            ? <>{orgName} has asked you to help confirm {applicantDisplayName}'s identity{liveRelationship ? <> as their <span className="font-medium text-foreground">{liveRelationship}</span></> : null}.</>
            : <>{applicantDisplayName} has asked you to help confirm this person's identity.</>}{" "}
          This carries <span className="font-medium text-foreground">no liability for you</span> — we simply want to verify that the person who invited you is who they say they are.
        </p>
      </motion.div>
      )}

      {/* Stepper — hidden on completion; the completion layout renders its own all-green stepper. */}
      {!showCompleted && !articlePresentation && (
      <div className="flex items-center justify-center gap-1">
        {steps.map((_, i) => (
          <div key={i} className="flex items-center gap-1">
            <button
              onClick={() => { if (i < current) setStep(i); }}
              disabled={i > current}
              className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                i < current ? "bg-trust-green text-trust-green-foreground cursor-pointer hover:ring-2 hover:ring-trust-green/40" :
                i === current && current === 4 ? "bg-trust-green-muted border-2 border-trust-green text-trust-green" :
                i === current ? "bg-primary text-primary-foreground" :
                "bg-muted text-muted-foreground"
              }`}
            >
              {i < current || (i === current && current === 4) ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
            </button>
            {i < steps.length - 1 && (
              <div className={`w-6 h-0.5 ${i < current ? "bg-trust-green" : "bg-border"}`} />
            )}
          </div>
        ))}
      </div>
      )}

      {/* No liability banner — hidden on step 0, on live steps 1-4 (per-step hero carries it), and on completion. */}
      {current !== 0 && !showCompleted && !articlePresentation && !(isLive && (current === 1 || current === 2 || current === 3 || current === 4)) && (
        <div className="bg-muted/50 border border-border rounded-lg p-3 flex items-start gap-2.5">
          <ShieldCheck className="w-4 h-4 text-trust-green shrink-0 mt-0.5" />
          <div className="text-xs text-muted-foreground space-y-0.5">
            <p className="font-medium text-foreground">No liability on your part</p>
            <p>Your response is one input in a broader review. You are not guaranteeing anything — we're simply checking that the person who sent this to you is actually the person they claim to be.</p>
          </div>
        </div>
      )}

      {showCompleted ? (
        articlePresentation ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="article-voucher-completion">
            <div className="article-voucher-completion-symbol">
              <Check aria-hidden="true" />
            </div>
            <h1>
              {liveOutcome === "unable_to_confirm"
                ? "Thanks for letting us know."
                : liveOutcome === "decline"
                  ? "Your response is recorded."
                  : `Thank you for vouching for ${applicantFirstName}.`}
            </h1>
            <p>
              {liveOutcome === "unable_to_confirm"
                ? `${orgName} has been told that you weren’t able to confirm this vouch. No further action is needed.`
                : liveOutcome === "decline"
                  ? `Your response has been shared with ${orgName}. ${applicantFirstName} will not be told whether you declined.`
                  : `Your vouch has been shared with ${orgName} as part of ${applicantFirstName}’s application review.`}
            </p>
          </motion.div>
        ) : (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center text-center space-y-7">
          {/* Context pill */}
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-card border border-border text-xs font-semibold text-foreground/80">
            <span className="w-1.5 h-1.5 rounded-full bg-trust-green" />
            {liveOutcome === "unable_to_confirm" || liveOutcome === "decline"
              ? <>Response to {orgName}</>
              : <>Vouching for {applicantFirstName} · {orgName}</>}
          </div>

          {/* Avatar with green check badge */}
          <div
            className="relative w-[88px] h-[88px] rounded-full flex items-center justify-center text-white text-[30px] font-extrabold"
            style={{
              background: "var(--flow-avatar-bg, linear-gradient(135deg, #7C3AED, #A78BFA))",
              boxShadow: "var(--flow-avatar-complete-shadow, 0 0 0 8px rgba(124,58,237,0.12), 0 4px 24px rgba(124,58,237,0.3))",
            }}
          >
            {applicantInitials}
            <span className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-trust-green border-2 border-background flex items-center justify-center">
              <Check className="w-3.5 h-3.5 text-trust-green-foreground" strokeWidth={3} />
            </span>
          </div>

          {/* Headline + sub */}
          <div className="space-y-3 max-w-[520px]">
            <h1 className="text-2xl sm:text-[28px] font-display font-extrabold text-foreground leading-tight">
              {liveOutcome === "unable_to_confirm"
                ? <>Thanks for taking a moment.</>
                : liveOutcome === "decline"
                  ? <>Response recorded.</>
                  : <>Thank you for vouching for {applicantFirstName}.</>}
            </h1>
            <p className="text-[15px] text-muted-foreground leading-relaxed">
              {liveOutcome === "unable_to_confirm"
                ? <>We've let {orgName} know you weren't able to confirm this vouch. No further action is needed.</>
                : liveOutcome === "decline"
                  ? <>Your response has been shared with {orgName}. {applicantFirstName} will not be told whether you declined.</>
                  : <>Your vouch has been submitted and will be shared with {orgName} as part of {applicantFirstName}'s application review.</>}
            </p>
          </div>

          {/* All-green completed stepper */}
          <div className="flex items-center justify-center gap-1">
            {steps.map((_, i) => (
              <div key={i} className="flex items-center gap-1">
                <div className="w-8 h-8 rounded-full flex items-center justify-center bg-background border-2 border-trust-green text-trust-green">
                  <Check className="w-4 h-4" strokeWidth={3} />
                </div>
                {i < steps.length - 1 && <div className="w-6 h-0.5 bg-trust-green" />}
              </div>
            ))}
          </div>

          {/* Confirmation card */}
          <Card className="w-full bg-card shadow-sm">
            <CardContent className="p-6 sm:p-8 space-y-4">
              <div className="rounded-2xl border-2 border-trust-green/30 bg-trust-green-muted/40 p-8 text-center space-y-4">
                <div className="w-12 h-12 rounded-full bg-trust-green mx-auto flex items-center justify-center">
                  <Check className="w-6 h-6 text-trust-green-foreground" strokeWidth={3} />
                </div>
                <h2 className="text-lg font-display font-bold text-foreground">
                  {liveOutcome === "unable_to_confirm" || liveOutcome === "decline"
                    ? "Response recorded"
                    : "Vouch submitted"}
                </h2>
                <p className="text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">
                  {liveOutcome === "unable_to_confirm"
                    ? <>Thank you for taking the time to respond. <span className="font-semibold text-foreground">No further action is needed from you.</span></>
                    : liveOutcome === "decline"
                      ? <>Thank you — your response was recorded. <span className="font-semibold text-foreground">{applicantFirstName} will not be told whether you declined.</span></>
                      : <>Thank you — your {identityVerified ? "verified " : ""}vouch helps the {copy.reviewingTeam} better understand {applicantFirstName}'s application. <span className="font-semibold text-foreground">Your input is one signal among many in their broader review.</span></>}
                </p>
              </div>
              <p className="text-xs text-muted-foreground text-center">
                You can close this window — there's nothing else you need to do.
              </p>
              {/* The only controls on this screen, and until now there was no
                  Reset among them: both the header's Reset and the step-0 one
                  sit inside `!showCompleted` blocks, so submitting a demo vouch
                  left no way back except the browser. A demo whose last screen
                  is a dead end cannot be shown twice.

                  The label also said "Back to dashboard" while navigating to
                  /demo — it was missed by the rename that moved the other demo
                  exits, because it is the only one that spelled it that way. */}
              {!isLive && (
                <div className="flex flex-wrap justify-center gap-2 pt-1">
                  <Button variant="outline" onClick={handleReset}>
                    <RotateCcw className="w-4 h-4 mr-1" /> Run it again
                  </Button>
                  {!implementationMode && (
                    <Button variant="ghost" onClick={() => navigate("/demo")}>
                      <ArrowLeft className="w-4 h-4 mr-1" /> Back to demos
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
        )
      ) : (
        <motion.div className="article-step-shell" key={current} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.25 }}>
          <Card className="article-step-card bg-card shadow-sm">
            <CardContent className="article-step-content p-6 space-y-5">
              {current !== 0 && !(isLive && (current === 1 || current === 2 || current === 3 || current === 4)) && (
                <div className="article-step-heading flex items-center gap-3">
                  {(() => { const Icon = steps[current].icon; return <div className="w-10 h-10 rounded-lg bg-trust-green-muted flex items-center justify-center"><Icon className="w-5 h-5 text-trust-green" /></div>; })()}
                  <div>
                    <h2 className="font-display font-semibold text-foreground">{steps[current].title}</h2>
                    <p className="text-xs text-muted-foreground">{steps[current].desc}</p>
                  </div>
                </div>
              )}

              {/* Step 0: Merged Welcome + Intent screen.
                  Three zones: identity receipt · pre-checked confirmation · "what's coming" info card.
                  See docs/voucher-flow-step1-merged.md. */}
              {current === 0 && (
                articlePresentation ? (
                  <div className="article-voucher-welcome">
                    <div className="article-voucher-invitation-summary">
                      <div className="article-voucher-summary-row">
                        <span>Vouching for</span>
                        <strong>{applicantDisplayName}</strong>
                      </div>
                      <div className="article-voucher-summary-row">
                        <label htmlFor="voucher-relationship-new">Your relationship</label>
                        {relationshipSelect(
                          "article-voucher-relationship-select rounded-md border border-slate-300 bg-white px-2 py-1 text-sm font-semibold",
                          "voucher-relationship-new",
                        )}
                      </div>
                      <div className="article-voucher-summary-row">
                        <span>Requested by</span>
                        <strong>{orgName}</strong>
                      </div>

                      <label className={`article-voucher-consent-row${emailConfirmed ? " is-checked" : ""}`}>
                        <input
                          type="checkbox"
                          checked={emailConfirmed}
                          onChange={(event) => setEmailConfirmed(event.target.checked)}
                        />
                        <span>
                          Yes, I am {voucherDisplayName}
                          {displayRelationship
                            ? ` and I am ${applicantFirstName}'s ${displayRelationship.toLowerCase()}.`
                            : "."}
                        </span>
                      </label>
                    </div>

                    {!emailConfirmed && (
                      <p className="article-voucher-details-help">
                        If these details look incorrect, contact {orgName} before continuing.
                      </p>
                    )}

                    <p className="article-voucher-welcome-note">
                      <ShieldCheck aria-hidden="true" />
                      About 2 minutes. Your answers go only to the {copy.reviewingTeam} reviewing the application.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-5">
                    {/* Zone 1 — read-only identity receipt */}
                    <div className="rounded-xl border border-border bg-muted/40 divide-y divide-border overflow-hidden">
                      <div className="flex items-center justify-between px-4 py-3">
                        <span className="text-[13px] text-muted-foreground font-medium">Your name</span>
                        <span className="text-sm font-semibold text-foreground text-right">{voucherName || "—"}</span>
                      </div>
                      <div className="flex items-center justify-between px-4 py-3">
                        <label htmlFor="voucher-relationship-classic" className="text-[13px] text-muted-foreground font-medium">
                          Your relationship to {applicantFirstName}
                        </label>
                        {relationshipSelect(
                          "rounded-md border border-border bg-background px-2 py-1 text-sm font-semibold text-foreground",
                          "voucher-relationship-classic",
                        )}
                      </div>
                      <div className="flex items-center justify-between px-4 py-3">
                        <span className="text-[13px] text-muted-foreground font-medium">Requested by</span>
                        <span className="text-sm font-semibold text-foreground text-right">{orgName}</span>
                      </div>
                    </div>

                    {/* Zone 2 — pre-checked, non-blocking confirmation */}
                    <div className="space-y-2">
                      <label
                        onClick={() => setEmailConfirmed(!emailConfirmed)}
                        className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border-[1.5px] cursor-pointer transition-colors ${
                          emailConfirmed
                            ? "border-primary bg-primary/5"
                            : "border-border hover:bg-muted/50"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={emailConfirmed}
                          readOnly
                          className="w-[18px] h-[18px] accent-primary cursor-pointer flex-shrink-0"
                        />
                        <span className="text-[15px] font-semibold text-foreground">
                          Yes, I am {voucherDisplayName}
                          {displayRelationship
                            ? ` and I am ${applicantFirstName}'s ${displayRelationship.toLowerCase()}.`
                            : "."}
                        </span>
                      </label>
                      {!emailConfirmed && (
                        <p className="text-xs text-muted-foreground px-1">
                          Please reach out to {orgName} if your details look incorrect.
                        </p>
                      )}
                    </div>

                    {/* Zone 3 — "Before we submit your vouch" info card */}
                    <div className="border-t border-border" />
                    <div className="voucher-before-submit-card rounded-xl border border-border bg-muted/40 p-5">
                      <p className="text-sm font-extrabold text-foreground mb-1.5">
                        Before we submit your vouch
                      </p>
                      {/* platform#605, the one #208 missed. Same predicate as the
                          hero sentence above: an org that does not require the
                          ID check must not be told every voucher completes it. */}
                      <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                        {idvRequiredForOrg
                          ? <>To keep every vouch trusted, we ask each voucher to complete two quick checks:</>
                          : <>To keep every vouch trusted, you'll be offered two quick checks:</>}
                      </p>
                      <div className="space-y-2.5 mb-3.5">
                        {[
                          { n: 1, label: "Verify your ID" },
                          { n: 2, label: "Connect at least one digital account (read-only)" },
                        ].map((step) => (
                          <div key={step.n} className="flex items-center gap-3">
                            <div className="w-6 h-6 rounded-full bg-primary/15 text-primary text-xs font-extrabold flex items-center justify-center flex-shrink-0">
                              {step.n}
                            </div>
                            <span className="text-sm font-semibold text-foreground">{step.label}</span>
                          </div>
                        ))}
                      </div>
                      <div className="border-t border-border pt-3.5 flex flex-col gap-2 text-xs text-muted-foreground leading-relaxed">
                        <p>Then we'll ask what you personally know about the applicant.</p>
                        <p>This helps the {copy.reviewingTeam} know the vouch came from a real, verified person and not a fake or anonymous submission.</p>
                        <p className="font-semibold text-foreground">Your answers are shared only with the {copy.reviewingTeam} reviewing the application.</p>
                      </div>
                    </div>
                  </div>
                )
              )}

              {/* Step 1: ID verification — Plaid IDV is the path forward.
                  PlaidIDVButton calls /verify/plaid-idv/link-token; on
                  success it flips idUploaded + selfieCompleted via its
                  onSuccess callback, which the Step-4 summary reads to
                  render the verified rows. */}
              {current === 1 && (
                <div className={articlePresentation ? "article-voucher-idv" : "space-y-4"}>
                  {/* Why this matters nudge */}
                  {articlePresentation ? (
                    <p className="article-voucher-idv-context">
                      We use this check only to confirm your identity, including details like your name and date of birth. It does not give {orgName} access to your document.
                    </p>
                  ) : <div className="voucher-idv-nudge flex items-start gap-3 rounded-xl border border-primary/20 bg-primary/5 p-3.5">
                    <div className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                      <Shield className="w-4 h-4 text-primary" />
                    </div>
                    <p className="text-[13px] leading-relaxed text-foreground">
                      <span className="font-semibold">Why this matters for {applicantFirstName}:</span>{" "}
                      Vouches tied to a verified identity carry more weight in the {copy.reviewingTeam}'s review. Tying yours to a verified identity confirms who submitted it; an anonymous vouch does not.
                    </p>
                  </div>}

                  {/* Plaid IDV — the only ID verification path today. Centered
                      single CTA. The legacy manual upload + selfie rows were
                      removed 2026-06-10 once IDV was confirmed working on UAT
                      (Plaid production env) — those rows were FE-only stubs
                      with no backend wiring. State vars (idUploaded /
                      selfieCompleted) stay because PlaidIDVButton's onSuccess
                      flips them so the Step-4 summary still renders the
                      "Government ID verified" / "Selfie completed" rows.
                      Manual fallback can return via a separate "Can't use
                      Plaid?" link if needed; not in scope today. */}
                  {isLive && (
                    <div className="max-w-sm mx-auto py-4">
                      <PlaidIDVButton
                        token={sessionToken ?? undefined}
                        verified={(idUploaded && selfieCompleted) || dataSourceVerified}
                        skipped={session?.voucher_idv_skipped === true}
                        onSuccess={({ documentary_passed, selfie_passed, data_source_passed }) => {
                          if (documentary_passed) setIdUploaded(true);
                          if (selfie_passed) setSelfieCompleted(true);
                          if (data_source_passed) setDataSourceVerified(true);
                        }}
                        onMaxRetriesReached={async () => {
                          // Call the backend skip endpoint so the vouch-outcome
                          // gate (§IDV-gate) accepts submission. Then refetch
                          // session status so Continue unblocks. Soft-fail; if
                          // it errors the voucher stays gated but PlaidIDVButton
                          // still shows the "Flagged for operator review" state
                          // so they at least have visual feedback.
                          if (!sessionToken) return;
                          try {
                            await applicantApi.plaidIdvSkip(sessionToken);
                            void refresh();
                          } catch {
                            /* see comment above — non-fatal */
                          }
                        }}
                      />
                    </div>
                  )}

                  {/* Demo: `PlaidIDVButton` mints a link token, so the whole
                      block above is isLive-gated and the ID step rendered with
                      no control on it at all — the one step of this flow that
                      is a hard gate, looking like nothing to do. Marking it
                      verified satisfies the same two flags the real button
                      sets, so Continue behaves the same way. */}
                  {!isLive && articlePresentation && (
                    <div className="article-voucher-idv-action">
                      {idUploaded && selfieCompleted ? (
                        <div className="article-voucher-idv-result">
                          <CheckCircle2 aria-hidden="true" />
                          <div>
                            <strong>ID check complete</strong>
                            <span>You can continue to the next step.</span>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="article-voucher-idv-start"
                          onClick={() => { setIdUploaded(true); setSelfieCompleted(true); }}
                        >
                          <ShieldCheck aria-hidden="true" />
                          <span>
                            <strong>Start ID check</strong>
                            <small>Government ID and a quick selfie</small>
                          </span>
                          <ChevronRight aria-hidden="true" />
                        </button>
                      )}
                    </div>
                  )}
                  {!isLive && !articlePresentation && (
                    <div className="max-w-sm mx-auto py-4">
                      <DemoIDVPanel
                        presentation={implementationMode ? "implementation" : "demo"}
                        verified={idUploaded && selfieCompleted}
                        onVerify={() => { setIdUploaded(true); setSelfieCompleted(true); }}
                      />
                    </div>
                  )}

                  {/* Privacy reassurance */}
                  <p className="voucher-idv-privacy text-xs text-muted-foreground/80 text-center leading-relaxed">
                    🔒 Used only to confirm your identity — including details like your name and date of birth. It is never stored after verification is complete.
                  </p>
                </div>
              )}

              {/* Step 2: Connect Accounts — flat row list, applicant-first frame.
                  Reuses existing OAuth (handleSourceClick → setPendingSource →
                  confirmConnect → applicantApi.startOAuth) and email-OTP
                  (EmailCodePanel inside pendingSource dialog) wiring.
                  See docs/voucher-flow-step3-connect-accounts.md. */}
              {current === 2 && (
                <div className={articlePresentation ? "article-voucher-accounts" : "space-y-5"}>
                  {/* Prose nudge */}
                  {!articlePresentation && <div className="pb-4 border-b border-border">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      Think of this like a modern reference letter.{" "}
                      <span className="font-semibold text-foreground">One connected account</span>{" "}
                      helps show that your vouch is coming from a real, established person.
                      The more you're comfortable connecting, the stronger {applicantFirstName}'s file becomes.
                    </p>
                  </div>}

                  {!articlePresentation && <AccountChecksTracker
                    emailVerified={true}
                    accountChecks={voucherAccountChecks}
                  />}


                  {/* The step-2 rows, minus any whose provider this environment
                      holds no credentials for. A `gatedOnAvailability` row is
                      ABSENT rather than disabled — the same choice the applicant
                      flow made — because a row the backend answers with
                      "Facebook OAuth not configured" is a dead end, and a
                      voucher is a guest doing someone else a favour: an option
                      that was never offered costs them nothing, an option that
                      fails after they click it costs them the favour.

                      `hideUnsatisfiable` is false because this step has no
                      manual-submit rows — every row here is OAuth or email OTP. */}
                  <div className="space-y-2.5">
                    {offerableRows.map((src) => {
                      const connected = isSourceConnected(src.id);
                      const RowIcon = src.icon;
                      const isVerifyCta = src.isEmailOtp;
                      if (articlePresentation) {
                        const brand = resolveBrandKey(src.id);
                        return (
                          <button
                            key={src.id}
                            type="button"
                            onClick={() => handleSourceClick(src)}
                            className={`article-choice-row article-voucher-account-row${connected ? " is-connected" : ""}`}
                          >
                            <span className="article-choice-row-main">
                              {connected ? (
                                <Check className="article-choice-status" aria-label="Connected" />
                              ) : brand ? (
                                <BrandIcon brand={brand} size={24} />
                              ) : (
                                <RowIcon aria-hidden="true" />
                              )}
                              <span>{src.name}</span>
                            </span>
                            {connected ? null : <ChevronRight aria-hidden="true" />}
                          </button>
                        );
                      }
                      return (
                        <button
                          key={src.id}
                          type="button"
                          onClick={() => handleSourceClick(src)}
                          className={`article-provider-row w-full flex items-center gap-3.5 rounded-2xl border p-4 text-left transition-colors ${
                            connected
                              ? "border-trust-green/40 bg-trust-green-muted/40"
                              : "border-border bg-card hover:border-primary/40 hover:bg-primary/[0.03]"
                          }`}
                        >
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                            connected ? "bg-trust-green/15" : src.iconBg
                          }`}>
                            {connected
                              ? <CheckCircle2 className="w-5 h-5 text-trust-green" />
                              : <RowIcon className={`w-5 h-5 ${src.iconFg}`} />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <p className="text-sm font-semibold text-foreground">{src.name}</p>
                              {src.tag && !connected && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold bg-primary/10 text-primary">
                                  {src.tag}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground leading-snug mt-0.5">{src.desc}</p>
                          </div>
                          {connected ? (
                            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-trust-green bg-trust-green-muted border border-trust-green/30 rounded-md px-2 py-1 shrink-0">
                              <Check className="w-3 h-3" /> Connected
                            </span>
                          ) : isVerifyCta ? (
                            <span className="inline-flex items-center text-[11px] font-bold text-trust-green-foreground bg-trust-green rounded-md px-2.5 py-1 shrink-0">
                              Verify
                            </span>
                          ) : (
                            <span className="inline-flex items-center text-[11px] font-semibold text-foreground/80 bg-muted rounded-md px-2.5 py-1 shrink-0">
                              Connect
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {/* Privacy reassurance */}
                  {articlePresentation && (
                    <p className="article-voucher-account-privacy">
                      <Lock aria-hidden="true" />
                      We use basic profile details only. We never read messages, posts, contacts, or private content.
                    </p>
                  )}
                  {!articlePresentation && <div className="rounded-xl border border-border bg-muted/40 p-4 flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <Lock className="w-4 h-4 text-primary" />
                    </div>
                    <div className="text-xs text-muted-foreground leading-relaxed space-y-1.5">
                      <p>
                        We only look at your <span className="font-semibold text-foreground">name, email, and basic account details like account age</span> — nothing else. We never read messages, contacts, purchases, or private content.
                      </p>
                      <p className="font-semibold text-foreground">
                        We disconnect everything the moment you hit Submit. Nothing about your accounts is changed.
                      </p>
                    </div>
                  </div>}
                </div>
              )}



              {/* Step 3: Questions */}
              {/* Step 3: Questions — matches mock voucher-flow-step4-questions.html.
                  Question text uses `{first}` placeholders so the applicant's
                  first name flows through both UI and `qa_responses` payload. */}
              {current === 3 && (
                articlePresentation ? (() => {
                  const question = questions[articleQuestionIndex];
                  const questionText = question.q.replace(/\{first\}/g, applicantFirstName || "them");
                  return (
                    <div className="article-voucher-question">
                      <p className="article-voucher-question-count">
                        Question {articleQuestionIndex + 1} of {questions.length}
                      </p>
                      <h2>{questionText}</h2>
                      <div className="article-voucher-answer-list">
                        {question.options.map((option) => {
                          const selected = answers[articleQuestionIndex] === option;
                          const soft = question.softOptions?.includes(option);
                          return (
                            <button
                              key={option}
                              type="button"
                              aria-pressed={selected}
                              className={`article-voucher-answer${selected ? " is-selected" : ""}${soft ? " is-soft" : ""}`}
                              onClick={() => {
                                if (answerOpensUnableToConfirm(articleQuestionIndex, option)) {
                                  setAnswers((previous) => ({ ...previous, 0: "No" }));
                                  setNoKnowDialog(true);
                                  return;
                                }
                                setAnswers((previous) => ({ ...previous, [articleQuestionIndex]: option }));
                              }}
                            >
                              <span>{option}</span>
                              {selected && <Check aria-hidden="true" />}
                            </button>
                          );
                        })}
                      </div>

                      <p className="article-voucher-question-reassurance">
                        Answer only from your own experience. Choose “Not sure” whenever it’s offered and fits.
                      </p>
                    </div>
                  );
                })() : (
                <div className="space-y-5">
                  <div className="bg-muted/40 border border-border rounded-xl p-4 flex gap-3 items-start">
                    <div className="w-8 h-8 rounded-full bg-trust-green-muted flex items-center justify-center shrink-0 mt-0.5">
                      <ShieldCheck className="w-4 h-4 text-trust-green" />
                    </div>
                    <div className="space-y-1">
                      {/* ⚠️ These are the liability and scope disclaimers, not
                          vocabulary. They were written against a tenant-
                          screening posture; the employment equivalent sits
                          under a different regime (FCRA/EEOC-shaped) and the
                          company constitution's sixth core value commits to
                          real legal advice before entering a vertical, not
                          after.

                          Juan's call, 2026-09-01, for the IDW demo: all six
                          are now vertical-NEUTRAL. "Leasing" came out and
                          "hiring" did NOT go in — nothing here asserts
                          anything about an employment decision, which is why
                          this is not the mechanical swap the note here used to
                          forbid. The payment/tenancy/lease-performance sentence
                          was dropped outright rather than reworded.

                          The one exception is the RECIPIENT in the review
                          paragraph, which goes through `copy.reviewingTeam`
                          like the other nine sites on this surface: naming who
                          receives the summary is a fact about routing, not a
                          claim about the decision.

                          Counsel still owes the employment wording; #177. */}
                      <p className="text-[13px] font-semibold text-foreground">You are not responsible for the decision</p>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        We are only asking what you personally know. <span className="font-semibold text-foreground/80">Not sure is always an acceptable answer.</span>
                      </p>
                    </div>
                  </div>

                  {(() => {
                    const q1Yes = answers[0] === "Yes";
                    // Default state: only Q1 is shown. Q2–Q7 + optional note
                    // reveal only when the voucher answers Q1 = "Yes". A "No"
                    // answer opens the unable-to-confirm modal (handled in the
                    // Q1 option click below) and never reveals the rest.
                    const visibleQuestions = q1Yes ? questions : questions.slice(0, 1);
                    return (
                      <>
                        {visibleQuestions.map((q, i) => {
                          const text = q.q.replace(/\{first\}/g, applicantFirstName || "them");
                          return (
                            <div key={i} className="space-y-2">
                              <p className="text-sm font-semibold text-foreground leading-snug">
                                <span className="text-muted-foreground font-medium mr-1.5">{i + 1}</span>
                                {text}
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {q.options.map((opt) => {
                                  const isSelected = answers[i] === opt;
                                  const isSoft = q.softOptions?.includes(opt);
                                  const handleClick = () => {
                                    if (answerOpensUnableToConfirm(i, opt)) {
                                      // Don't persist "No" — modal-driven path.
                                      // Cancelling resets Q1 to empty (per spec).
                                      setAnswers((prev) => ({ ...prev, 0: "No" }));
                                      setNoKnowDialog(true);
                                      return;
                                    }
                                    setAnswers((prev) => ({ ...prev, [i]: opt }));
                                  };
                                  return (
                                    <button
                                      key={opt}
                                      onClick={handleClick}
                                      className={`px-4 py-1.5 text-[13px] border rounded-full transition-colors font-semibold ${
                                        isSelected
                                          ? "border-primary bg-primary text-primary-foreground"
                                          : isSoft
                                            ? "border-border bg-background text-muted-foreground hover:text-foreground hover:border-muted-foreground"
                                            : "border-border bg-background text-foreground hover:border-primary hover:text-primary hover:bg-primary/5"
                                      }`}
                                    >
                                      {opt}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}

                      </>
                    );
                  })()}

                  <div className="text-xs text-muted-foreground space-y-1 bg-muted/40 border border-border rounded-xl p-3.5">
                    <p>· Your response is one input in a broader review and carries no liability.</p>
                    <p>· {applicantFirstName || "The applicant"} will not be told whether you declined to confirm.</p>
                    <p>· We do not share your private information with the applicant.</p>
                  </div>
                </div>
                )
              )}


              {/* Step 5 of 5: Review & submit — completed-only summary rows, green submit CTA.
                  Reuses existing handleSubmitOutcome("confirm") wiring; no new endpoints.
                  See docs/voucher-flow-step5-submit.md. */}
              {current === 4 && (() => {
                const maskedEmail = verifiedVoucherEmails[0]?.detail ?? null;
                const answerCount = Object.keys(answers).length;
                const summaryRows: { label: string; sub?: string }[] = [];
                if (emailConfirmed || isLive || maskedEmail) {
                  summaryRows.push({
                    label: "Email confirmed",
                    sub: maskedEmail ? `${maskedEmail} verified` : "Invitation email verified",
                  });
                }
                if (idUploaded) summaryRows.push({ label: "Government ID verified", sub: "Name matched to invitation" });
                if (selfieCompleted) summaryRows.push({ label: "Selfie completed", sub: "Liveness check passed" });
                if (dataSourceVerified) summaryRows.push({ label: "Identity verified", sub: "Confirmed against trusted data sources" });
                step2ConnectedSources.forEach((s) => {
                  summaryRows.push({ label: `${s.name} connected`, sub: s.desc });
                });
                if (answerCount > 0) {
                  // How long, and how recently (questions 3 and 4).
                  const keyAnswers = [answers[2], answers[3]].filter(Boolean).join(" · ");
                  summaryRows.push({
                    label: `${answerCount} question${answerCount === 1 ? "" : "s"} answered`,
                    sub: keyAnswers || undefined,
                  });
                }
                return articlePresentation ? (
                  <div className="article-voucher-review">
                    <p className="article-voucher-review-label">What you’re sharing</p>
                    <div className="article-voucher-review-rows">
                      {summaryRows.map((row) => (
                        <div key={row.label} className="article-voucher-review-row">
                          <Check aria-hidden="true" />
                          <div>
                            <strong>{row.label}</strong>
                            {row.sub && <span>{row.sub}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="article-voucher-review-note">
                      By submitting, you confirm that these responses reflect what you personally know. {orgName} makes the final decision.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-0">
                    <div className="text-[12px] font-bold uppercase tracking-[0.08em] text-muted-foreground mb-4">
                      What you're submitting
                    </div>
                    <div className="divide-y divide-border/60">
                      {summaryRows.map((row) => (
                        <div key={row.label} className="flex items-center gap-3 py-[11px]">
                          <div className="w-6 h-6 rounded-full bg-trust-green-muted border border-trust-green flex items-center justify-center shrink-0">
                            <Check className="w-2.5 h-2.5 text-trust-green" strokeWidth={3} />
                          </div>
                          <div className="min-w-0">
                            <div className="text-[13.5px] font-semibold text-foreground leading-tight">{row.label}</div>
                            {row.sub && <div className="text-[12px] text-muted-foreground mt-0.5">{row.sub}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                    <hr className="border-border/60 my-5" />
                    <div className="bg-muted/50 border border-border rounded-xl p-4 flex gap-2.5 items-start">
                      <ShieldCheck className="w-[15px] h-[15px] text-muted-foreground shrink-0 mt-0.5" />
                      <p className="text-[12.5px] text-muted-foreground leading-relaxed">
                        By submitting, you confirm your responses are truthful to the best of your knowledge.{" "}
                        <span className="font-bold text-foreground">You are not responsible for the decision.</span>{" "}
                        Your input is one signal among many in a broader review.
                      </p>
                    </div>
                  </div>
                );
              })()}

              {/* Primary CTA */}
              {current === 0 ? (
                <div className={articlePresentation ? "article-voucher-actions" : "space-y-3"}>
                  <p className={articlePresentation ? "article-voucher-agreement" : "text-xs text-muted-foreground text-center leading-relaxed"}>
                    By continuing, I agree to the DigaVouch User Agreement, Privacy Policy, and Data Policy.
                  </p>
                  <Button
                    onClick={isLive ? handleConsent : handleNext}
                    className={articlePresentation ? "article-voucher-primary" : "w-full h-12 text-base font-bold rounded-xl"}
                    disabled={consenting || (lookRules.identityConfirmationRequired && !emailConfirmed)}
                  >
                    {consenting
                      ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Recording consent…</>
                      : <>{articlePresentation ? "Continue" : "Continue — help them now"} <ArrowRight className="w-4 h-4 ml-2" /></>}
                  </Button>
                  <button
                    type="button"
                    onClick={() => setDeclineDialog(true)}
                    disabled={consenting || submitting !== null}
                    className={articlePresentation ? "article-voucher-decline" : "block w-full text-center text-sm font-semibold text-foreground/80 hover:text-destructive underline underline-offset-4 decoration-border hover:decoration-destructive/60 transition-colors disabled:opacity-50 py-1"}
                  >
                    No, I don't know this person or can't vouch for them
                  </button>
                  {!articlePresentation && <div className="voucher-step0-footnote pt-3 border-t border-border space-y-2">
                    <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground text-center leading-relaxed">
                      <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0" />
                      {idvRequiredForOrg
                        ? <>Every vouch on this request includes an identity check to keep the process fair and trustworthy. </>
                        : <>Every vouch on this request is reviewed carefully to keep the process fair and trustworthy. </>}
                      No liability on your part—you're simply confirming what you know. DigaVouch provides this summary to the {copy.reviewingTeam} but does not make the final decision.
                    </p>
                  </div>}
                </div>
              ) : current === 1 ? (
                <div className={articlePresentation ? "article-voucher-actions" : "space-y-3"}>
                  {!articlePresentation && <p className="voucher-idv-reassurance flex items-center justify-center gap-1.5 text-xs text-muted-foreground text-center">
                    <ShieldCheck className="w-3.5 h-3.5" />
                    {idvRequiredForOrg
                      ? <>Every voucher completes this step the same way.</>
                      : <>A quick ID check strengthens {applicantFirstName}'s vouch.</>}
                  </p>}
                  {/* IDV gate — when the org has voucher_idv_required=true,
                      Continue stays disabled until either (a) IDV completes
                      (idUploaded && selfieCompleted), or (b) the FE called
                      /verify/plaid-idv/skip after exhausting retries (which
                      flips session.voucher_idv_skipped=true). Server gate at
                      /sessions/:token/vouch-outcome is defense-in-depth — any
                      hand-crafted POST gets 422 IDV_REQUIRED. */}
                  {(() => {
                    const idvDone = (idUploaded && selfieCompleted) || dataSourceVerified;
                    const idvSkipped = session?.voucher_idv_skipped === true;
                    // Fail CLOSED while the IDV requirement is still unknown.
                    // In live mode the session drives the gate; until it has
                    // loaded we can't know whether this org requires IDV, so we
                    // disable Continue rather than flashing it enabled then
                    // disabled (observed on UAT cold reopen) — which also let a
                    // fast click slip past the gate before status arrived.
                    const idvStatusKnown = !isLive || !!session;
                    const gated = !idvStatusKnown
                      || (session?.voucher_idv_required === true
                        && !idvDone
                        && !idvSkipped);
                    return (
                      <>
                        <Button
                          onClick={handleNext}
                          disabled={gated}
                          className={articlePresentation ? "article-voucher-primary" : "w-full h-12 text-base font-bold rounded-xl"}
                        >
                          {articlePresentation ? "Continue" : `Continue — help ${applicantFirstName} now`} <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                        {gated && (
                          <p className="text-xs text-amber-700 text-center leading-snug">
                            Identity verification is required by {orgName} before you can continue.
                          </p>
                        )}
                      </>
                    );
                  })()}
                  {!articlePresentation && <p className="voucher-liability-note text-xs text-muted-foreground/80 text-center leading-relaxed">
                    No liability on your part. You're simply confirming what you personally know.
                  </p>}
                </div>
              ) : current === 4 ? (
                <div className={articlePresentation ? "article-voucher-actions" : "space-y-4 pt-2"}>
                  {/* Where every answer is required, Submit waits for them.
                      Answers live in this page, so a reload on this screen
                      loses them while ?step= keeps the voucher here: say what
                      is missing and offer the one way back to it. */}
                  {missingAnswers.length > 0 && submitting === null && (
                    <div className="voucher-missing-answers" role="status">
                      <p>
                        {missingAnswers.length === 1
                          ? "1 question still needs an answer."
                          : `${missingAnswers.length} questions still need an answer.`}
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          setArticleQuestionIndex(missingAnswers[0]);
                          setStep(3);
                        }}
                      >
                        Go to question {missingAnswers[0] + 1}
                      </button>
                    </div>
                  )}
                  <Button
                    onClick={() => handleSubmitOutcome("confirm")}
                    className={articlePresentation ? "article-voucher-primary" : "w-full h-auto py-4 bg-trust-green text-trust-green-foreground hover:bg-trust-green/90 rounded-xl text-[17px] font-extrabold gap-2.5"}
                    disabled={submitting !== null || questionsSubmitBlocked(lookRules, answers, questions.length)}
                  >
                    {submitting === "confirm"
                      ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Submitting…</>
                      : articlePresentation
                        ? <>Submit vouch <ArrowRight aria-hidden="true" /></>
                        : <><Check className="w-[18px] h-[18px]" strokeWidth={2.5} /> Submit my vouch for {applicantFirstName}</>}
                  </Button>
                  <button
                    type="button"
                    onClick={() => setStep(3)}
                    disabled={submitting !== null}
                    className={articlePresentation ? "article-voucher-back-link" : "block w-full text-center text-[13px] text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"}
                  >
                    ← Go back and change something
                  </button>
                </div>
              ) : current === 2 ? (
                <div className={articlePresentation ? "article-voucher-actions" : "space-y-3"}>
                  <Button
                    onClick={handleNext}
                    className={articlePresentation ? "article-voucher-primary" : "w-full h-12 text-base font-bold rounded-xl"}
                  >
                    {articlePresentation ? "Continue" : `Continue — strengthen ${applicantFirstName}'s vouch`} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  <p className={articlePresentation ? "article-voucher-account-reassurance" : "text-xs text-muted-foreground/80 text-center leading-relaxed"}>
                    You are not responsible for the decision. You're only confirming what you personally know about {applicantFirstName}.
                  </p>
                  <button
                    type="button"
                    onClick={handleNext}
                    className={articlePresentation ? "article-voucher-skip" : "block w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors"}
                  >
                    {articlePresentation ? "Skip this step" : "I'd rather skip this step"}
                  </button>
                </div>
              ) : current === 3 ? (
                <div className={articlePresentation ? "article-voucher-actions" : "space-y-2"}>
                  <Button
                    onClick={articlePresentation
                      ? () => {
                          if (articleQuestionIndex < questions.length - 1) {
                            setArticleQuestionIndex((index) => index + 1);
                            return;
                          }
                          setStep(4);
                        }
                      : handleNext}
                    disabled={questionsContinueBlocked(lookRules, answers, articleQuestionIndex)}
                    className={articlePresentation ? "article-voucher-primary" : "w-full h-12 text-base font-bold rounded-xl"}
                  >
                    {articlePresentation && articleQuestionIndex === questions.length - 1 ? "Review vouch" : articlePresentation ? "Continue" : "Continue — almost done"} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  {!articlePresentation && <p className="text-xs text-muted-foreground/80 text-center leading-relaxed">
                    You are not responsible for the decision. You're only sharing what you personally know about {applicantFirstName || "them"}.
                  </p>}
                </div>
              ) : (
                <Button onClick={handleNext} className="w-full">
                  Continue <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}

            </CardContent>
          </Card>
        </motion.div>
      )}
    </div>
  );
}
